import Layout from "@/shared/pages/Layout.jsx";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage, LanguageProvider } from "@/shared/components/shared/LanguageContext";

// Engine Pages
import EngineDashboard from "@/shared/pages/EngineDashboard";
import EngineMerchants from "@/shared/pages/EngineMerchants";
import EngineTransactions from "@/shared/pages/EngineTransactions";
import EngineWithdrawals from "@/shared/pages/EngineWithdrawals";
import EngineSettings from "@/shared/pages/EngineSettings";
import EngineDeposits from "@/shared/pages/EngineDeposits";
import EngineResellers from "@/shared/pages/EngineResellers";
import EngineProviders from "@/shared/pages/EngineProviders";
import EngineRouting from "@/shared/pages/EngineRouting";
import EngineWebhooks from "@/shared/pages/EngineWebhooks";
import EngineSettlements from "@/shared/pages/EngineSettlements";
import Notifications from "@/shared/pages/Notifications";

// Login Pages
import EngineLogin from "@/shared/pages/EngineLogin";
import StaffLogin from "@/shared/pages/StaffLogin";

import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const PAGES = {
  EngineDashboard,
  EngineMerchants,
  EngineTransactions,
  EngineWithdrawals,
  EngineSettings,
  EngineDeposits,
  EngineResellers,
  EngineProviders,
  EngineRouting,
  EngineWebhooks,
  EngineSettlements,
  Notifications,
  EngineLogin,
  StaffLogin,
};

function _getCurrentPage(url) {
  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }
  let urlLastPart = url.split('/').pop();
  if (urlLastPart.includes('?')) {
    urlLastPart = urlLastPart.split('?')[0];
  }

  const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
  return pageName || 'EngineDashboard';
}

// Component to handle root route - redirects authenticated users to dashboard, shows login otherwise
function RootRouteContent() {
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = auth.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // If authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/EngineDashboard" replace />;
  }

  // Otherwise, show login page
  return <EngineLogin />;
}

function RootRoute() {
  return (
    <LanguageProvider>
      <RootRouteContent />
    </LanguageProvider>
  );
}

function EngineAppContent() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  // Check if current route is a login page
  const isLoginPage = ['/', '/login', '/staff-login', '/EngineLogin', '/StaffLogin'].includes(location.pathname);

  // If it's a login page, render without Layout
  if (isLoginPage) {
    return (
      <Routes>
        {/* Root route - shows login or redirects to dashboard if authenticated */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/login" element={<EngineLogin />} />
        <Route path="/EngineLogin" element={<EngineLogin />} />
        <Route path="/staff-login" element={<StaffLogin panelType="ENGINE" />} />
        <Route path="/StaffLogin" element={<StaffLogin panelType="ENGINE" />} />
      </Routes>
    );
  }

  // Otherwise, render with Layout
  return (
    <Layout currentPageName={currentPage} skipLayoutPages={["Notifications"]}>
      <Routes>
        {/* Engine Routes */}
        <Route path="/EngineDashboard" element={<EngineDashboard />} />
        <Route path="/dashboard" element={<EngineDashboard />} />
        <Route path="/EngineMerchants" element={<EngineMerchants />} />
        <Route path="/merchants" element={<EngineMerchants />} />
        <Route path="/EngineTransactions" element={<EngineTransactions />} />
        <Route path="/transactions" element={<EngineTransactions />} />
        <Route path="/EngineWithdrawals" element={<EngineWithdrawals />} />
        <Route path="/withdrawals" element={<EngineWithdrawals />} />
        <Route path="/EngineSettings" element={<EngineSettings />} />
        <Route path="/settings" element={<EngineSettings />} />
        <Route path="/EngineDeposits" element={<EngineDeposits />} />
        <Route path="/deposits" element={<EngineDeposits />} />
        <Route path="/EngineResellers" element={<EngineResellers />} />
        <Route path="/resellers" element={<EngineResellers />} />
        <Route path="/EngineProviders" element={<EngineProviders />} />
        <Route path="/providers" element={<EngineProviders />} />
        <Route path="/EngineRouting" element={<EngineRouting />} />
        <Route path="/routing" element={<EngineRouting />} />
        <Route path="/EngineWebhooks" element={<EngineWebhooks />} />
        <Route path="/webhooks" element={<EngineWebhooks />} />
        <Route path="/EngineSettlements" element={<EngineSettlements />} />
        <Route path="/settlements" element={<EngineSettlements />} />
        <Route path="/Notifications" element={<EngineLayout currentPage="Notifications"><Notifications /></EngineLayout>} />
        <Route path="/notifications" element={<EngineLayout currentPage="Notifications"><Notifications /></EngineLayout>} />

        {/* Catch-all redirect to root (login) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function EngineApp() {
  return (
    <Router>
      <EngineAppContent />
    </Router>
  );
}

